import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { BehaviorSubject, Observable, retry } from 'rxjs';
import { GooglePlaceSearchResponse } from '../models/google-places/google-place-search-response.model';
import { GooglePlace } from '../models/google-places/google-place.model';
import { GeoLocation } from 'account-hybrid/common/models/location.model';
import { debounceTime, finalize, map, take, tap } from 'rxjs/operators';
import { EntriesPerPlace } from '../models/google-places/entries-per-place.model';
import { SpacesService } from 'account-hybrid/common/components/spaces';

// instance created per places-editor, should not be providedIn: 'root'
@Injectable()
export class GooglePlacesService {
    googlePlacesUrl = `${this.environment.apiBaseUrl}/googleplaces/nearby`;
    searchRadius = 321869;

    places$ = new BehaviorSubject<GooglePlace[]>([]);
    entriesPerPlace$ = new BehaviorSubject<EntriesPerPlace[]>([]);
    loading$ = new BehaviorSubject<boolean>(false);

    currentSearchTerm: string;
    currentLocation: GeoLocation;
    nextPageToken: string;

    constructor(private http: HttpClient,
                private spacesService: SpacesService,
                @Inject(ENVIRONMENT_TOKEN) private environment: Environment) {
    }

    searchNearbyPlaces(term: string, location: GeoLocation) {
        if ((this.currentSearchTerm == term) && (this.currentLocation.lng == location.lng && this.currentLocation.lat == location.lat) && !this.nextPageToken) {
            return;
        } else if(this.currentSearchTerm != term) {
            this.nextPageToken = null;
        }

        this.currentSearchTerm = term;
        this.currentLocation = location;
        let params = new HttpParams()
            .set('radius', encodeURIComponent(this.searchRadius.toString()))
            .set('keyword', term)
            .set('lng', `${this.currentLocation.lng}`)
            .set('lat', `${this.currentLocation.lat}`);

        if (this.nextPageToken) {
            params = params.set('pagetoken', this.nextPageToken);
        }
        this.loading$.next(true);
        this.http.get<GooglePlaceSearchResponse>(this.googlePlacesUrl, { params, withCredentials: false })
            .pipe(
                map(response => {
                    // retry logic needed because next page is not available immediately
                    if (response.status === 'INVALID_REQUEST') {
                        throw new Error('Invalid request - pagetoken not ready');
                    }
                    return response;
                }),
                retry({ count: 3, delay: 1000 }),
                take(1),
                tap(response => {
                    const data = response.results.sort((a, b) => a.name.localeCompare(b.name));
                    if (params.get('pagetoken') != null) {
                        this.places$.next([
                            ...this.places$.getValue(),
                            ...data.map(item => GooglePlace.dtoToPlain(item))
                        ]);
                    } else {
                        this.places$.next([...(data.map(item => GooglePlace.dtoToPlain(item)))]);
                        this.nextPageToken = response?.next_page_token;
                    }
                }),
                finalize(() => this.loading$.next(false))
            ).subscribe({
            error: (err) => {
                console.error('Google Places search failed after retries', err);
            }
        });
    }

    getEntriesPerPlace(googlePlaceIds: string[], companyVendorUuid: string | number) {
        if (googlePlaceIds?.length > 0) {
            let params = new HttpParams()
                .set('placeIds', googlePlaceIds.join(','));

            this.http.get<EntriesPerPlace[]>(`${this.environment.apiBaseUrl}/${this.spacesService.getPropertyUuid()}/properties/${this.spacesService.getPropertyUuid()}/vendors/${companyVendorUuid.toString()}/places/counts`, { params })
                .pipe(
                    map(response => this.entriesPerPlace$.next(response.map(({ googlePlaceId, count }) => ({ googlePlaceId, count })))),
                    take(1)
                ).subscribe();
        }
    }
}
